import {
  AudioWaveIcon,
  DocumentIcon,
  ImageIcon,
  VideoIcon,
} from 'components/icons'
import {
  ContrastLevel,
  discrepancyActionsEnum,
  discrepancySeverityEnum,
  discrepancyStatusEnum,
  Levels,
  taskStatusEnum,
  taskTypesEnum,
  templateStatusEnum,
  TENANTS,
} from './enums'
import { Routes } from './routesEndpoints'
import { HeadPartItems, UserResponse } from './types'
import { DynamicColor, DynamicObject } from './types/globalSettings'
import { ConfigTableParams } from './types/subCategories'
import { saveAs } from 'file-saver'
import { TFunction } from 'i18next'
import { userPermission } from 'utils/function'

export const levelNames = (t: TFunction): { [key: number]: string } => ({
  [Levels.category]: t('category'),
  [Levels.subCategory]: t('sub_category'),
  [Levels.section]: t('section'),
  [Levels.field]: t('field'),
  [Levels.type]: t('type'),
  [Levels.ruleField]: t('rule_field'),
})

export const headCells = (t: TFunction): HeadPartItems[] => [
  {
    id: 'text',
    label: t('name'),
    css: {
      width: '40%',
    },
  },
  {
    id: 'section',
    label: 'Sub Category',
    css: {
      width: '23%',
    },
  },
  {
    id: 'status',
    label: 'Status',
    css: {
      width: '12%',
    },
  },
  {
    id: 'createdAt',
    label: 'Date Created',
    css: {
      width: '12%',
    },
  },
]

export const categoryTableHeader = (t: TFunction): HeadPartItems[] => [
  {
    id: 'name',
    label: t('name'),
    css: {
      width: '50%',
    },
    sort: true,
  },
  {
    id: 'subCategory',
    label: t('sub_category'),
    css: {
      width: '23%',
    },
  },
  {
    id: 'status',
    label: t('status'),
    css: {
      width: '12%',
    },
    sort: true,
  },
  {
    id: 'created_at',
    label: t('created_at'),
    css: {
      width: '12%',
    },
    sort: true,
  },
]

export const subCategoryTableHeader = (t: TFunction): HeadPartItems[] => [
  {
    id: 'name',
    label: t('name'),
    css: {
      width: '37%',
    },
    sort: true,
  },
  {
    id: 'type',
    label: t('type'),
    css: {
      width: '40%',
    },
  },
  {
    id: 'section',
    label: t('section'),
    css: {
      width: '80%',
    },
  },
  {
    id: 'status',
    label: t('status'),
    css: {
      width: '12%',
    },
    sort: true,
  },
  {
    id: 'created_at',
    label: t('created_at'),
    css: {
      width: '12%',
    },
    sort: true,
  },
]

export const sectionTableHeader = (t: TFunction): HeadPartItems[] => [
  {
    id: 'name',
    label: t('name'),
    css: {
      width: '40%',
    },
    sort: true,
  },
  {
    id: 'field',
    label: t('fields'),
    css: {
      width: '23%',
    },
  },
  {
    id: 'status',
    label: t('status'),
    css: {
      width: '12%',
    },
    sort: true,
  },
  {
    id: 'created_at',
    label: t('created_at'),
    css: {
      width: '12%',
    },
    sort: true,
  },
]

export const fieldTableHeader = (t: TFunction): HeadPartItems[] => [
  {
    id: 'name',
    label: t('name'),
    css: {
      width: '40%',
    },
    sort: true,
  },
  {
    id: 'field',
    label: t('fields_type'),
    css: {
      width: '23%',
    },
  },
  {
    id: 'status',
    label: t('status'),
    css: {
      width: '12%',
    },
    sort: true,
  },
  {
    id: 'created_at',
    label: t('created_at'),
    css: {
      width: '12%',
    },
    sort: true,
  },
]

export const typesTableHeader = (t: TFunction): HeadPartItems[] => [
  {
    id: 'name',
    label: t('name'),
    css: {
      width: '40%',
    },
  },
  {
    id: 'status',
    label: t('status'),
    css: {
      width: '20%',
    },
  },
  {
    id: 'createdAt',
    label: t('created_at'),
    css: {
      width: '27%',
    },
  },
]

export const discrepancyPaginationParam = {
  page: 1,
  limit: 12,
}
export const discrepancyMainPaginationParam = {
  page: 1,
  limit: 20,
}

export const paginationParam = {
  page: 1,
  limit: 20,
}

export const paginationParamWithSearch = {
  page: 1,
  limit: 20,
  search: '',
}

export const paginationExtendedParam = {
  page: 1,
  limit: 100,
}

export const paginationUndefinedParam = {
  page: undefined,
  limit: undefined,
}

export const params: ConfigTableParams = {
  id: 0,
  ...paginationExtendedParam,
  search: '',
}

export const FieldTypesName: DynamicObject = {
  NUMERIC: 'Numeric',
  TEXT: 'Text',
  COMMENTS: 'Comments',
  FILE: 'File',
  DROPDOWN_SINGLE_SELECTION: 'Dropdown Single Selection',
  DROPDOWN_MULTIPLE_SELECTION: 'Dropdown Multiple Selection',
  DROPDOWN_MULTIPLE_FEATURES: 'Dropdown Multiple Features',
  CHECKBOX: 'Checkbox',
  RADIO: 'Radio',
  CALENDAR: 'Calendar',
  DAY_CALENDAR: 'Day Calendar',
  VIEW_360: 'View 360° (inspection)',
  LOCATION: 'Location',
  RATING: 'Rating',
  PICTURE_ANNOTATION: 'Picture Annotation (inspection)',
}

export const StatusLabel: DynamicObject = {
  true: 'Active',
  false: 'Inactive',
  enabled: 'Enabled',
  disabled: 'Disabled',
  published: 'Published',
  INTERNAL: 'Internal',
  EXTERNAL: 'External',
  DEFAULT: 'Default',
  MOTHER: 'Mother',
  CUSTOMER: 'Customer',
  INSPEQ: 'Inspeq',
  PENDING: 'Pending',
  ACCEPTED: 'Accepted',
  REJECTED: 'Rejected',
  PENDING_SIGNUP: 'Pending Signup',
  PROFILE_PENDING: 'Profile Pending',
  CONFIRMED: 'Confirmed',
  VERIFICATION_PENDING: 'Verification Pending',
  REVERT: 'Reverted',
  INPROGRESS: 'In Progress',
  COMPLETED: 'Completed',
  RENTAL: 'Rental',
  NETWORK: 'Network',
  INSPECTIONS: 'Inspections',
  USERS: 'Users',
  [discrepancySeverityEnum.MINOR]: 'Minor',
  [discrepancySeverityEnum.MODERATE]: 'Moderate',
  [discrepancySeverityEnum.MAJOR]: 'Major',
  [discrepancySeverityEnum.CRITICAL]: 'Critical',
  [taskTypesEnum.GENERAL_INSPECTION]: 'General Inspection',
  [taskTypesEnum.RENTAL_CHECK_IN]: 'Check In',
  [taskTypesEnum.RENTAL_CHECK_OUT]: 'Check Out',
  [taskStatusEnum.OWNER_APPROVAL]: 'Owner Approval',
  [taskStatusEnum.CUSTOMER_APPROVAL]: 'Customer Approval',
  [taskStatusEnum.REASSIGNED]: 'Reassigned',
  [taskStatusEnum.CANCELED]: 'Canceled',
  DONE: 'Done',
  [discrepancyActionsEnum.NO_ACTION_REQUIRED]: 'No Action Required',
  [discrepancyActionsEnum.ATTENTION_REQUIRED]: 'Immediate Attention Required',
  [discrepancyActionsEnum.MAINTENANCE_REQUIRED]: 'Schedule Maintenance',
  [discrepancyActionsEnum.REPLACEMENT_REQUIRED]: 'Replacement Required',
  [discrepancyStatusEnum.INPROGRESS]: 'In Progress',
  [discrepancyStatusEnum.IN_PROGRESS]: 'In Progress',
  [discrepancyStatusEnum.PARTIALLY_RESOLVED]: 'Partially Resolved',
  [discrepancyStatusEnum.RESOLVED]: 'Resolved',
  [discrepancyStatusEnum.PENDING_REVIEW]: 'Pending Review',
  ACTIVE: 'Active',
  DRAFTED: 'Drafted',
  ARCHIVED: 'Archive',
  INACTIVE: 'Inactive',
}

export const StatusColor: DynamicColor = {
  [StatusLabel.true]: 'success',
  [StatusLabel.false]: 'error',
  [StatusLabel.enabled]: 'success',
  [StatusLabel.disabled]: 'warning',
  [StatusLabel.published]: 'success',
  [StatusLabel.INTERNAL]: 'success',
  [StatusLabel.EXTERNAL]: 'error',
  [StatusLabel.DEFAULT]: 'gray',
  [StatusLabel.MOTHER]: 'gray',
  [StatusLabel.INSPEQ]: 'gray',
  [StatusLabel.CUSTOMER]: 'gray',
  [StatusLabel.PENDING]: 'warning',
  [StatusLabel.ACCEPTED]: 'success',
  [StatusLabel.REJECTED]: 'error',
  [StatusLabel.PENDING_SIGNUP]: 'error',
  [StatusLabel.PROFILE_PENDING]: 'gray',
  [StatusLabel.VERIFICATION_PENDING]: 'warning',
  [StatusLabel.CONFIRMED]: 'success',
  [StatusLabel.REVERT]: 'gray',
  [StatusLabel.INPROGRESS]: 'gray',
  [StatusLabel.COMPLETED]: 'success',
  [StatusLabel.GENERAL_INSPECTION]: 'primary',
  [StatusLabel.RENTAL]: 'success',
  [StatusLabel.RENTAL_CHECK_IN]: 'warning',
  [StatusLabel.RENTAL_CHECK_OUT]: 'error',
  [StatusLabel.NETWORK]: 'warning',
  [StatusLabel.INSPECTIONS]: 'success',
  [StatusLabel.USERS]: 'gray',
  [StatusLabel.OWNER_APPROVAL]: 'error',
  [StatusLabel.CUSTOMER_APPROVAL]: 'error',
  [StatusLabel.REASSIGNED]: 'purple',
  [StatusLabel.CANCELED]: 'error',
  [StatusLabel.MINOR]: 'success',
  [StatusLabel.MODERATE]: 'warning',
  [StatusLabel.MAJOR]: 'error',
  [StatusLabel.CRITICAL]: 'errorDark',
  [StatusLabel.NO_ACTION_REQUIRED]: 'success',
  [StatusLabel.ATTENTION_REQUIRED]: 'warning',
  [StatusLabel.MAINTENANCE_REQUIRED]: 'error',
  [StatusLabel.REPLACEMENT_REQUIRED]: 'warning',
  [StatusLabel[discrepancyStatusEnum.PARTIALLY_RESOLVED]]: 'success',
  [StatusLabel[discrepancyStatusEnum.RESOLVED]]: 'success',
  [StatusLabel[discrepancyStatusEnum.PENDING_REVIEW]]: 'error',
  [StatusLabel[discrepancyStatusEnum.INPROGRESS]]: 'primary',
  [StatusLabel[discrepancyStatusEnum.IN_PROGRESS]]: 'primary',
} as DynamicColor

export const ColorCode = {
  [discrepancySeverityEnum.MINOR]: '$success500',
  [discrepancySeverityEnum.MODERATE]: '$warning500',
  [discrepancySeverityEnum.MAJOR]: '$error500',
  [discrepancySeverityEnum.CRITICAL]: '$error700',
}

export const FieldStatusOption = (t: TFunction) => [
  { label: t('pending'), value: discrepancyStatusEnum.PENDING, enabled: true },
  {
    label: t('in_progress'),
    value: discrepancyStatusEnum.IN_PROGRESS,
    enabled: true,
  },
  {
    label: t('no_action_req'),
    value: discrepancyStatusEnum.NO_ACTION_REQUIRED,
    enabled: true,
  },
  { label: t('done'), value: discrepancyStatusEnum.DONE, enabled: true },
]
export const TemplateStatusOptions = (t: TFunction) => [
  { label: t('active'), value: templateStatusEnum.ACTIVE, enabled: true },
  {
    label: 'In Active',
    value: templateStatusEnum.INACTIVE,
    enabled: true,
  },
  {
    label: t('drafted'),
    value: templateStatusEnum.DRAFTED,
    enabled: true,
  },
  { label: 'acrhived', value: templateStatusEnum.ARCHIVED, enabled: true },
]

export const tagHeadCells = (t: TFunction): HeadPartItems[] => [
  {
    id: 'name',
    label: t('name'),
    css: {
      width: '30%',
    },
  },
  {
    id: 'divisions',
    label: t('divisions'),
    css: {
      width: '30%',
    },
  },
  {
    id: 'categories',
    label: t('categories'),
    css: {
      width: '30%',
    },
  },
  {
    id: 'sub_categories',
    label: t('sub_categories'),
    css: {
      width: '30%',
    },
  },
  {
    id: 'createdAt',
    label: t('created_at'),
    css: {
      width: '12%',
    },
  },
]
export const businessDivisionHeadCells = (t: TFunction): HeadPartItems[] => [
  {
    id: 'name',
    label: t('division_name'),
  },
  {
    id: 'status',
    label: t('status'),
  },
  {
    id: 'createdAt',
    label: t('created_at'),
  },
]
export const fieldRulesTableHeader = (t: TFunction): HeadPartItems[] => [
  {
    id: 'name',
    label: t('name'),
    css: {
      width: '31%',
    },
  },
  {
    id: 'type',
    label: t('status'),
    css: {
      width: '20%',
    },
  },
  {
    id: 'operator',
    label: t('operators'),
    css: {
      width: '20%',
    },
  },
  {
    id: 'compare_value',
    label: t('operators'),
    css: {
      width: '0%',
    },
  },
]
export const userHeadCells = (t: TFunction): HeadPartItems[] => [
  {
    id: 'name',
    label: t('user_name'),
    css: {
      width: '30%',
    },
    sort: true,
  },
  {
    id: 'email',
    label: t('email_address'),
    css: {
      width: '30%',
    },
    sort: true,
  },
  {
    id: 'status',
    label: t('status'),
    css: {
      width: '30%',
    },
    sort: true,
  },
  {
    id: 'network',
    label: t('network_and_clusters'),
    css: {
      width: '30%',
    },
  },
]

export const imsHeadCells = (t: TFunction): HeadPartItems[] => [
  {
    id: 'title',
    label: t('listing_title'),
    css: {
      width: '30%',
    },
    sort: true,
  },
  {
    id: 'category',
    label: t('category'),
    css: {
      width: '30%',
    },
  },
  {
    id: 'subCategory',
    label: t('sub_category'),
    css: {
      width: '30%',
    },
  },
  {
    id: 'quantity',
    label: t('availability_and_quantity'),
    css: {
      width: '30%',
    },
  },
  {
    id: 'tags',
    label: t('tags'),
    css: {
      width: '30%',
    },
  },
  {
    id: 'status',
    label: t('status'),
    css: {
      width: '30%',
    },
  },
]
export const imsDiscounListingCells = (t: TFunction): HeadPartItems[] => [
  {
    id: 'title',
    label: t('listing_title'),
    css: {
      width: '20%',
    },
    sort: true,
  },
  {
    id: 'category',
    label: t('category'),
    css: {
      width: '20%',
    },
  },
  {
    id: 'price',
    label: 'pirce_privacy',
    css: {
      width: '20%',
    },
  },
]
export const imsDiscountCells = (t: TFunction): HeadPartItems[] => [
  {
    id: 'title',
    label: 'title',
    css: {
      width: '20%',
    },
    sort: true,
  },
  {
    id: 'count',
    label: t('listing_count'),
    css: {
      width: '20%',
    },
  },
  {
    id: 'cluster',
    label: t('clusters'),
    css: {
      width: '20%',
    },
  },
  {
    id: 'status',
    label: t('status'),
    css: {
      width: '20%',
    },
  },
  {
    id: 'discount',
    label: t('discount'),
    css: {
      width: '20%',
    },
  },
  {
    id: 'startDate',
    label: t('start_date'),
    css: {
      width: '20%',
    },
  },
  {
    id: 'endDate',
    label: t('end_date'),
    css: {
      width: '20%',
    },
  },
]
export const videoHeadCells = (t: TFunction): HeadPartItems[] => [
  {
    id: 'title',
    label: 'video_title',
    css: {
      width: '20%',
    },
    sort: true,
  },
  {
    id: 'url',
    label: t('video_urls'),
    css: {
      width: '80%',
    },
  },
]
export const permissionHeadCells = (t: TFunction): HeadPartItems[] => [
  {
    id: 'name',
    label: t('permission_name'),
    css: {
      width: '30%',
    },
  },
  // {
  //   id: 'module',
  //   label: t('modules'),
  //   css: {
  //     width: '30%',
  //   },
  // },
]

export const clusterHeadCells = (t: TFunction): HeadPartItems[] => [
  {
    id: 'name',
    label: t('cluster_name'),
    css: {
      width: '30%',
    },
    sort: true,
  },
  {
    id: 'networks',
    label: t('network_members'),
    css: {
      width: '30%',
    },
  },
  {
    id: 'type',
    label: t('type'),
    css: {
      width: '30%',
    },
    sort: true,
  },
  {
    id: 'expiry_date',
    label: t('expiry'),
    css: {
      width: '30%',
    },
    sort: true,
  },
  {
    id: 'rules',
    label: t('rules'),
    css: {
      width: '30%',
    },
  },
]
export const networkHeadCells = (t: TFunction): HeadPartItems[] => [
  {
    id: 'name',
    label: t('network_name'),
    // sort: true,
  },
  {
    id: 'domain',
    label: t('tenant'),
    // sort: true,
  },
  {
    id: 'cluster',
    label: t('cluster'),
  },
  {
    id: 'status',
    label: t('status'),
    sort: true,
  },
  {
    id: 'user_mappings',
    label: t('members'),
  },
]
export const rulesHeadCells = (t: TFunction): HeadPartItems[] => [
  {
    id: 'network_a',
    label: t('network_name'),
    css: {
      width: '30%',
    },
  },
  {
    id: 'related',
    label: '',
    css: {
      width: '30%',
    },
  },
  {
    id: 'network_b',
    label: 'Associated with',
    css: {
      width: '30%',
    },
  },
  {
    id: 'status',
    label: 'Status',
    css: {
      width: '30%',
    },
  },
]
export const memberHeadCells = (t: TFunction): HeadPartItems[] => [
  {
    id: 'name',
    label: t('member_name'),
    css: {
      width: '30%',
    },
  },
  {
    id: 'phone',
    label: t('contact_number'),
    css: {
      width: '30%',
    },
  },
  {
    id: 'status',
    label: t('invitation_status'),
    css: {
      width: '30%',
    },
  },
]

export const jobOrdersHeadCells = (t: TFunction): any => [
  {
    id: 'job_order_id',
    label: t('job_order_id'),
  },
  {
    id: 'title',
    label: t('title'),
  },
  {
    id: 'type',
    label: t('type'),
  },
  {
    id: 'status',
    label: t('status'),
  },
  {
    id: 'assignee',
    label: t('assignee'),
  },
  {
    id: 'priority',
    label: t('priority'),
  },
  {
    id: 'due_date',
    label: t('due_date'),
  },
  {
    id: 'manufacturer',
    label: t('manufacturer'),
  },
  {
    id: 'serial_number',
    label: t('serial_number'),
  },
  {
    id: 'category',
    label: t('category'),
  },
  {
    id: 'sub-category',
    label: t('sub_category'),
  },

  {
    id: 'created_by',
    label: t('created_by'),
  },
  {
    id: 'watchers',
    label: t('watchers'),
  },
  {
    id: 'end_date',
    label: t('rental_start_end'),
  },
]

export const jobOrdersAddInventoryHeadCells: any = [
  {
    id: 'title',
    label: 'Title',
    style: {
      span: {
        pl: '$space$44',
      },
    },
  },
  {
    id: 'serial_number',
    label: 'Serial Number',
  },
  {
    id: 'category',
    label: 'Category / Sub Category',
  },
]

export const allReportsHeadCells = (t: TFunction): any => [
  {
    id: 'job_order_id',
    label: t('job_order_id'),
  },
  {
    id: 'title',
    label: t('title'),
  },
  {
    id: 'created_at',
    label: t('created_at'),
  },
  {
    id: 'serial_number',
    label: t('serial_number'),
  },
  {
    id: 'manufacturer',
    label: t('manufacturer'),
  },
  {
    id: 'category',
    label: t('category'),
  },
  {
    id: 'sub-category',
    label: t('sub_category'),
  },
  {
    id: 'created_by',
    label: t('assignee'),
  },
  {
    id: 'equipment_no',
    label: t('equipment_no'),
  },
  {
    id: 'is_private',
    label: t('private'),
  },
]

export const DiscrepancyReportsHeadCells = (t: TFunction): any => [
  {
    id: 'report_id',
    label: t('report_id'),
    sort: true,
  },
  {
    id: 'primary_report_id',
    label: t('primary_report_id'),
  },
  {
    id: 'secondary_report_id',
    label: t('secondary_report_id'),
  },
  {
    id: 'status',
    label: t('status'),
  },
  {
    id: 'discrepancy_count',
    label: t('discrepancies'),
  },
  {
    id: 'user_name',
    label: t('created_by'),
  },
  {
    id: 'created_at',
    label: t('created_at'),
  },
  {
    id: 'updated_at',
    label: t('last_updated_at'),
  },
  {
    id: 'primary_category',
    label: t('primary_categories'),
  },
  {
    id: 'secondary_category',
    label: t('secondary_categories'),
  },
]

export const DiscrepancyReportDetailsHeadCells = (
  t: TFunction
): HeadPartItems[] => [
  {
    id: 'fields',
    label: t('fields'),
  },
  {
    id: 'primary_report',
    label: t('primary_report'),
  },
  {
    id: 'secondary_report',
    label: t('secondary_report'),
  },
  {
    id: 'suggested_action',
    label: t('suggested_actions'),
  },
  {
    id: 'severity',
    label: t('severity'),
  },
  {
    id: 'status',
    label: t('status'),
  },
]

export const generateDiscrepancyReportHeadCells = (t: TFunction): any => [
  {},
  {
    id: 'id',
    label: 'Report ID',
    sort: true,
  },
  {
    id: 'listing_title',
    label: 'Report title',
  },
  {
    id: 'equipment_no',
    label: 'Inventory title',
  },
  {
    id: 'category',
    label: 'Category',
  },
  {
    id: 'sub_category',
    label: t('sub_category'),
  },
]

export const templateHeadCells = (t: TFunction): HeadPartItems[] => [
  {
    id: 'template_id',
    label: t('template_id'),
  },
  { id: 'name', label: t('template_title'), sort: true },
  {
    id: 'category',
    label: t('category'),
  },
  {
    id: 'sub_category',
    label: t('sub_category'),
  },

  {
    id: 'sections',
    label: t('sections'),
  },
  {
    id: 'fields',
    label: t('field_count'),
  },
  {
    id: 'total_submissions',
    label: t('template_submission'),
  },
  {
    id: 'created_by',
    label: t('create_by'),
  },
  {
    id: 'created_at',
    label: t('create_at'),
  },
  {
    id: 'status',
    label: t('status'),
  },
  {
    id: 'updated_at',
    label: t('updated_at'),
  },
]

export const TemplateSubmissionHeadCells: HeadPartItems[] = [
  {
    id: 'submitter',
    label: 'Submitter',
  },
  {
    id: 'dateTime',
    label: 'Submission Date and Time',
  },
  {
    id: 'status',
    label: 'Submission Status',
  },
]

export const contrastLevelNames = (
  t: TFunction
): { [key: number]: string } => ({
  [ContrastLevel.veryPoor]: t('very_poor'),
  [ContrastLevel.poor]: t('poor'),
  [ContrastLevel.good]: t('good'),
  [ContrastLevel.veryGood]: t('very_good'),
  [ContrastLevel.super]: t('very_good'),
})

export const OperatorNames: DynamicObject = {
  EQUALS: 'Equals',
  NOT_EQUALS: 'Not Equals',
  GREATER_THAN: 'Greater than',
  GREATER_THAN_EQUALS: 'Greater than Equals',
  LESS_THAN: 'Less than',
  LESS_THAN_EQUALS: 'Less than Equals',
  RANGE: 'Range',
  CONTAINS: 'Contains',
  DOES_NOT_CONTAIN: 'Does not contain',
  STARTS_WITH: 'Starts with',
  ENDS_WITH: 'Ends with',
  CONTENT_LENGTH: 'Content Length',
}

export const ConfiguratorItems = (t: TFunction) => [
  {
    label: t('category_management'),
    route: Routes.manage,
  },
  {
    label: t('marketplace_divisions'),
    route: Routes.businessDivision,
  },
  {
    label: t('tags'),
    route: Routes.tags,
  },
  {
    label: t('versions'),
    route: Routes.versions,
  },
]
export const SettingItems = (t: TFunction, user: UserResponse) => [
  {
    label: t('general'),
    route: Routes.general,
  },
  {
    label: t('tenants'),
    route: Routes.domains,
    is_disabled: !(
      userPermission(
        user?.user_permissions || [],
        TENANTS.VIEW,
        user?.user_type || ''
      ) ||
      userPermission(
        user?.user_permissions || [],
        TENANTS.UPDATE,
        user?.user_type || ''
      )
    ),
  },
]
export const linkItems = [
  {
    label: 'Manage Configurator',
    route: Routes.manage,
  },
  {
    label: 'Business Division',
    route: Routes.businessDivision,
  },
  {
    label: 'Tags',
    route: Routes.tags,
  },
  {
    label: 'Versions',
    route: Routes.versions,
  },
]
export const UserItems = (t: TFunction) => [
  {
    label: t('field_configuration'),
    route: Routes.userProfile,
  },
  {
    label: t('all_users'),
    route: Routes.userList,
  },
  {
    label: t('roles_and_permissions'),
    route: Routes.role,
  },
  // {
  //   label: 'Networks',
  //   route: Routes.network,
  // },
]
export const templateItems = (t: TFunction) => [
  {
    label: t('my_templates'),
    route: Routes.myTemplate,
  },
  {
    label: t('library'),
    route: Routes.userList,
  },
  {
    label: t('settings'),
    route: Routes.role,
  },
  // {
  //   label: 'Networks',
  //   route: Routes.network,
  // },
]
export const InventoryItems = [
  {
    label: 'Inventories',
    route: Routes.inventoryList,
  },
  {
    label: 'Discount',
    route: Routes.inventoryDiscount,
    is_disabled: true,
  },
]
export const NotificationItems = [
  {
    label: 'Notifications',
    route: Routes.notificationList,
  },
]

export const AuthenticationItems = [
  {
    label: 'User Profile',
    route: Routes.userProfile,
  },
]

export const NetworkItems = [
  {
    label: 'Clusters',
    route: Routes.clusters,
  },
  {
    label: 'Networks',
    route: Routes.networksList,
  },
  {
    label: 'Cluster Rules',
    route: Routes.clusterRules,
    is_disabled: true,
  },
]

export const clusterRulesHeader = (t: TFunction): HeadPartItems[] => [
  {
    id: 'description',
    label: t('rule_description'),
    css: {
      width: '30%',
    },
  },
  {
    id: 'operator',
    label: t('operator'),
    css: {
      width: '30%',
    },
  },
]

export const priceRulesHeader = (t: TFunction): HeadPartItems[] => [
  {
    id: 'cluster_id',
    label: t('cluster'),
  },
  {
    id: 'price',
    label: t('price'),
  },
  {
    id: 'dateRange',
    label: t('date_range'),
  },
]

export const versionHeadCells = (t: TFunction): HeadPartItems[] => [
  {
    id: 'name',
    label: t('name'),
  },
  {
    id: 'type',
    label: t('type'),
  },
  {
    id: 'status',
    label: t('status'),
  },
  {
    id: 'createdAt',
    label: t('created_at'),
  },
]

export const versionTypeList = (t: TFunction) => [
  {
    label: t('all'),
    value: 'all',
  },
  {
    label: t('marketplace'),
    value: 'config',
  },
  {
    label: t('inspection'),
    value: 'inspeq',
  },
]

export const inspectionItems = (t: TFunction) => [
  {
    label: t('job_orders'),
    route: Routes.jobOrders,
  },
  {
    label: t('reports'),
    route: Routes.reports,
  },
  {
    label: t('discrepancies'),
    route: Routes.discrepancyReports,
  },
]

export const domainHeadCells = (t: TFunction): HeadPartItems[] => [
  {
    id: 'name',
    label: t('name'),
  },
]

export const profileFieldTableHeader = (t: TFunction): HeadPartItems[] => [
  {
    id: 'title',
    label: t('name'),
    css: {
      width: '40%',
    },
    sort: true,
  },
  {
    id: 'field',
    label: t('field_type'),
    css: {
      width: '23%',
    },
  },
  {
    id: 'status',
    label: t('status'),
    css: {
      width: '12%',
    },
    sort: true,
  },
  {
    id: 'created_at',
    label: t('created_at'),
    css: {
      width: '12%',
    },
    sort: true,
  },
]

export const getIconComponent = (type?: string): JSX.Element => {
  switch (type) {
    case 'IMAGE':
      return <ImageIcon width={20} height={20} />
    case 'VIDEO':
      return <VideoIcon width={20} height={20} />
    case 'AUDIO':
      return <AudioWaveIcon width={20} height={20} />
    default:
      return <DocumentIcon width={20} height={20} />
  }
}

export const getFileName = (url: string): string =>
  url.substring(url.lastIndexOf('/') + 1)

export const downloadFile = (url: string): void => {
  const filename = getFileName(url)
  saveAs(url, filename)
}

export const TimeZoneOptions = (Intl as any)
  .supportedValuesOf('timeZone' as any)
  .map((tz: any) => ({
    label: tz.replace(/_/g, ' '),
    value: tz,
  }))

export const LinkExpiryOptions = [
  { label: '1 hour', value: 1 },
  { label: '6 hours', value: 6 },
  { label: '12 hours', value: 12 },
  { label: '24 hours', value: 24 },
  { label: '48 hours', value: 48 },
  { label: '72 hours', value: 72 },
]
